<template>
  <b-modal
      :visible="visible"
      :title="song ? `${song.artist_name} — ${song.title}` : 'История в чартах'"
      size="lg"
      hide-footer
      @change="$emit('change', $event)"
      @show="load"
  >
    <b-overlay :show="isLoading" rounded="sm">
      <div v-if="song">
        <div class="mb-1">
          <b-badge
              v-for="stat in song.stats"
              :key="stat.chart_slug"
              variant="light-primary"
              class="mr-50"
          >
            {{ stat.chart_name }}: #{{ stat.peak }} · {{ stat.weeks_total }} нед.
            <template v-if="stat.weeks_at_1"> · {{ stat.weeks_at_1 }} на №1</template>
          </b-badge>
        </div>
        <vue-apex-charts
            v-if="series.length"
            type="line"
            height="260"
            :options="chartOptions"
            :series="series"
        />
        <b-table
            small
            striped
            :items="history"
            :fields="fields"
            class="mt-1"
            style="max-height: 300px; overflow-y: auto; display: block;"
        />
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import { BModal, BOverlay, BBadge, BTable } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { computed, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import chartsApi from './chartsApi'

export default {
  components: {
    BModal, BOverlay, BBadge, BTable, VueApexCharts,
  },
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    visible: { type: Boolean, default: false },
    songId: { type: [Number, String], default: null },
  },
  setup(props) {
    const toast = useToast()
    const isLoading = ref(false)
    const song = ref(null)
    const history = ref([])

    // Название чарта по slug — берём из song.stats (там оно уже пришло из Chart::name), фолбэк — сам slug
    const chartNameBySlug = computed(() => {
      const map = {}
      ;(song.value ? song.value.stats : []).forEach(stat => { map[stat.chart_slug] = stat.chart_name })
      return map
    })
    const chartName = slug => chartNameBySlug.value[slug] || slug

    const fields = [
      { key: 'chart_slug', label: 'Чарт', formatter: value => chartName(value) },
      { key: 'week_start', label: 'Неделя' },
      { key: 'position', label: 'Позиция' },
    ]

    const load = async () => {
      if (!props.songId) return
      // Сбрасываем данные предыдущей песни, иначе под оверлеем видна старая история
      song.value = null
      history.value = []
      isLoading.value = true
      try {
        const res = await chartsApi.fetchSong(props.songId)
        song.value = res.data.data.song
        history.value = res.data.data.history
      } catch (e) {
        toast({
          component: ToastificationContent,
          props: { title: 'Не удалось загрузить историю чарта', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        isLoading.value = false
      }
    }

    // Одна линия на чарт, ось X — даты недель
    const series = computed(() => {
      const byChart = {}
      history.value.forEach(row => {
        if (!byChart[row.chart_slug]) byChart[row.chart_slug] = []
        byChart[row.chart_slug].push({ x: row.week_start, y: row.position })
      })
      return Object.keys(byChart).map(slug => ({ name: chartName(slug), data: byChart[slug] }))
    })

    const chartOptions = {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      xaxis: { type: 'datetime' },
      // №1 сверху
      yaxis: { reversed: true, min: 1, forceNiceScale: true },
      stroke: { width: 2 },
      markers: { size: 3 },
      tooltip: { x: { format: 'dd.MM.yyyy' } },
    }

    return {
      isLoading, song, history, fields, load, series, chartOptions,
    }
  },
}
</script>
